import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal, ModalContent, ModalClose } from '@/components/modal'
import { Button } from '@/components/button/Button'
import { MatterSelectDropdown } from '@/components/matter-select/MatterSelectDropdown'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectChatCreateMattersSortedByName } from '@/matter/store/selectors'
import { MattersActions, MatterDialogType } from '@/matter/store/slice'
import { kMattersRoute } from '@/matter/constants'

type NewChatSelectMatterModalBaseProps = {
  /**
   * Whether the modal is open
   */
  open?: boolean
  /**
   * Callback function to handle the open state change
   */
  onOpenChange: (open: boolean) => void
  /**
   * Called when the user dismisses the modal without continuing (Cancel, Manage Matters, or Create new matter).
   * Parent should close the modal and run any side effects (e.g. close mobile menu).
   */
  onDismiss: () => void
  /**
   * Called when the continue button is clicked
   * (This button is only enabled when a matter is selected)
   * @param matterId - The ID of the selected matter
   */
  onContinue: (matterId: string) => void
  /**
   * Whether to switch the active matter after creating a new matter from this modal.
   * Defaults to true. Pass false when the modal is opened from a context where
   * switching the active matter would be disruptive (e.g. med job "Ask Assistant").
   */
  switchToMatterOnCreate?: boolean
  /**
   * Whether to auto-select a newly created matter in the dropdown after it's created.
   * Defaults to false. Pass true when the modal stays open after matter creation
   * so the user doesn't have to manually select the new matter (e.g. med job "Ask Assistant").
   */
  autoSelectCreatedMatter?: boolean
}

/**
 * When showExistingChatsHint is true, shows a hint about existing unassigned chats.
 * When omitted or false, the hint sentence is not shown (e.g. when opening from a med job context).
 */
type NewChatSelectMatterModalProps = NewChatSelectMatterModalBaseProps & { showExistingChatsHint?: boolean }

export const NewChatSelectMatterModal = ({
  open = false,
  onOpenChange,
  onDismiss,
  onContinue,
  showExistingChatsHint = false,
  switchToMatterOnCreate = true,
  autoSelectCreatedMatter = false,
}: NewChatSelectMatterModalProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const matters = useAppSelector(selectChatCreateMattersSortedByName)
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null)
  const prevMatterIdsRef = useRef(new Set(matters.map((m) => m.id)))

  // Auto-select the newly created matter in the dropdown so the user doesn't have to manually select it
  useEffect(() => {
    if (autoSelectCreatedMatter && open) {
      const prevIds = prevMatterIdsRef.current
      const newMatter = matters.find((m) => !prevIds.has(m.id))
      if (newMatter) setSelectedMatterId(newMatter.id)
    }
    prevMatterIdsRef.current = new Set(matters.map((m) => m.id))
  }, [matters, autoSelectCreatedMatter, open])

  const handleContinueClick = () => {
    if (!selectedMatterId) return
    onContinue(selectedMatterId)
    setSelectedMatterId(null)
  }

  const handleCancelClick = () => {
    setSelectedMatterId(null)
    onDismiss()
  }

  const handleCreateMatterClick = () => {
    dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Create, switchToMatterOnCreate }))
    if (switchToMatterOnCreate) onDismiss()
  }

  const handleManageMattersClick = () => {
    setSelectedMatterId(null)
    onDismiss()
    // Navigate to the matters page
    navigate(kMattersRoute)
  }

  const existingChatsHint = showExistingChatsHint ? ' Existing unassigned chats can still be viewed and moved into a Matter to continue working.' : ''

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent title="New work belongs in a matter" width="md" height="max-md">
        <div className="flex flex-col gap-6">
          <p className="text-brand-neutral-950 self-stretch text-base font-normal leading-6">
            {`To keep your work organized and case-specific, new chats must be created inside a Matter.${existingChatsHint}`}
          </p>

          <div>
            <MatterSelectDropdown
              className="z-[1001]" // display above the modal overlay
              items={matters}
              onSelect={setSelectedMatterId}
              onCreateNewMatter={handleCreateMatterClick}
              placeholder="Select a matter to continue"
              selectedItemId={selectedMatterId}
              variant="field"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="w-full sm:w-auto">
              <Button variant="plain" color="primary" onClick={handleManageMattersClick} className="w-full sm:w-auto">
                Manage Matters
              </Button>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
              <ModalClose asChild>
                <Button variant="outlined" color="primary" onClick={handleCancelClick} className="w-full sm:w-auto">
                  Cancel
                </Button>
              </ModalClose>
              <Button onClick={handleContinueClick} className="w-full sm:w-auto" disabled={!selectedMatterId}>
                Continue
              </Button>
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
